import React from 'react';

import { NavigateFunction } from 'react-router-dom';

import { Chip, FontAwesomeIcon } from '@appcharge/shared-ui';
import { CellContext } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { EToasterStatus, showToast } from 'utils/showToast';

import Checkbox from 'components/Checkbox/Checkbox';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'components/ui';

import { ContentTableDataType } from './ContentTable';

const statusMap: Record<string, 'success' | 'primary' | 'warning'> = {
  visible: 'success',
  hidden: 'primary',
  scheduled: 'warning'
};

export const getContentTableColumns = (
  handleDeleteSelectedContent: (id: string) => void,
  navigate: NavigateFunction,
  pagesData: { id: string; pageName: string }[] = []
) => [
    // {
    //   accessorKey: 'checkbox',
    //   header: () => <Checkbox variant="outlined" />,
    //   cell: () => <Checkbox variant="outlined" />
    // },
    {
      accessorKey: 'title',
      header: 'Content Name',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) =>
        cell.getValue() as string
    },
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }: any) => (
        <div
          className="group flex items-center justify-between w-full"
          data-testid={`ContentTable-IdCell-${row.original.id}`}
        >
          <span className="truncate max-w-[120px]">{row.original.id}</span>
          <FontAwesomeIcon
            icon="fa-regular fa-copy"
            className="cursor-pointer text-base-mutedForeground hover:text-base-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`ContentTable-CopyId-${row.original.id}`}
            onClick={() => {
              navigator.clipboard.writeText(row.original.id);
              showToast({
                message: 'Content ID copied to clipboard',
                status: EToasterStatus.SUCCESS
              });
            }}
          />
        </div>
      )
    },
    {
      accessorKey: 'pages',
      header: 'Page',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) => {
        const pageIds = cell.getValue() as string[];
        const isValidPages = Array.isArray(pageIds) && Array.isArray(pagesData);
        const pageNames = isValidPages
          ? pageIds
            .map((id) => pagesData.find((page) => page.id === id)?.pageName)
            .filter(Boolean)
            .join(', ')
          : '';
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <div className="w-[120px] overflow-hidden text-ellipsis whitespace-nowrap hover:z-10">
                  {pageNames}
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="center"
                className="rounded-md border border-base-popover bg-base-popover shadow-md py-[6px] px-[12px]"
              >
                {pageNames}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created Date',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) =>
        dayjs(cell.getValue() as string)
          .utc()
          .format('MMM DD, YYYY')
    },
    {
      accessorKey: 'publishDate',
      header: 'Last published',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) =>
        cell.getValue()
          ? dayjs(cell.getValue() as string)
            .utc()
            .format('MMM DD, YYYY')
          : ''
    },
    {
      accessorKey: 'tag',
      header: 'Tag',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) => {
        const tagValue = cell.getValue() as string;
        if (!tagValue) return null;
        return (
          <Chip
            status="primary"
            className="bg-[rgba(64,64,64,0.20)] text-neutral-700"
          >
            {tagValue}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'state',
      header: 'Status',
      cell: (cell: CellContext<Partial<ContentTableDataType>, unknown>) => (
        <Chip
          variant="dot"
          status={statusMap[cell.getValue() as string] || 'primary'}
          className="capitalize"
        >
          {cell.getValue() as string}
        </Chip>
      )
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: any) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => navigate(`./form/${row.original.id}`)
            },
            {
              label: 'Duplicate',
              handler: () => navigate(`./dup/${row.original.id}`)
            },
            {
              label: 'Delete',
              handler: () => {
                handleDeleteSelectedContent(row.original.id);
              }
            }
          ]}
        />
      )
    }
  ];
